import type {
  BillingPaywallState,
  BillingSubscriptionState,
  CardOnFile,
  ScheduledPlanChange,
  UnifiedBillingState,
} from './browser-types'
import { emitBrowserMainStoreSet, ensureStoreSnapshotNamespace } from './browser-store'
import { fetchBrowserApi } from './browser-http'

const BROWSER_BILLING_STATE_KEY = 'highlight:browser-billing-state'
const LEGACY_BILLING_PLANS = new Set(['unlimited', 'pro', 'pro-monthly', 'pro-yearly'])

// Mirrors getMeetingHoursForPlan(plan, 'v2-revamp') in @highlight/common (this file
// intentionally avoids that import; browser-billing-mirrors.test.ts pins the copies
// to their source). The browser runtime has no LaunchDarkly client; browser-initial-store
// seeds the revamp edition, the only one sold, so this table carries the revamp's
// allowances. null = unlimited.
export const MEETING_HOURS_BY_PLAN: Record<string, number | null> = {
  free: 10,
  pro: 30,
  'pro-trial': 30,
  lite: 20,
  pro2: 200,
  max: 200,
  'lite-trial': 20,
  'pro2-trial': 200,
  eat: null,
}

const getMeetingHoursForPlan = (plan: unknown): number | null =>
  typeof plan === 'string' && plan in MEETING_HOURS_BY_PLAN ? MEETING_HOURS_BY_PLAN[plan] : MEETING_HOURS_BY_PLAN.free

// Mirrors the paywall math constants in main/utils/billing (same no-import rule).
export const AUDIO_MINUTES_METRIC_NAME = 'Audio Note Minutes'
const MINUTES_PER_HOUR = 60
const FULL_USAGE_PERCENT = 100
const CREDIT_WARNING_PERCENT = 80

// Mirrors isTrialPlan in @highlight/common (same no-import rule; pinned in
// browser-billing-mirrors.test.ts).
export const isTrialPlan = (plan: unknown): boolean =>
  plan === 'pro-trial' || plan === 'lite-trial' || plan === 'pro2-trial'

// Mirrors planEndsAt in @highlight/common (same no-import rule; pinned in
// browser-billing-mirrors.test.ts), over the raw profile this file works with.
export const planEndsAt = (profile: Record<string, unknown>): string | undefined => {
  if (typeof profile.trialEndsAt === 'string') return profile.trialEndsAt
  if (typeof profile.cancelsAt === 'string') return profile.cancelsAt
  const currentPeriod = profile.currentPeriod as { endingAt?: string } | undefined
  return currentPeriod?.endingAt
}

const unwrapSuccessData = <T>(payload: unknown): T | null => {
  if (!payload || typeof payload !== 'object') {
    return null
  }
  const body = payload as { success?: boolean; data?: T }
  if (body.success === false) {
    return null
  }
  if (body.success === true) {
    return (body.data ?? null) as T | null
  }
  if ('data' in body) {
    return (body.data ?? null) as T | null
  }
  return payload as T
}

export const buildDefaultPaywallState = (): BillingPaywallState => ({
  plan: undefined,
  isTrial: false,
  credits: { used: 0, total: 0, remaining: 0, percentage: 0 },
  meetingHours: { used: 0, total: 0, remaining: 0, percentage: 0 },
  edition: 'v2-revamp',
  atWarning: false,
  atLimit: false,
  nagBarsDismissed: {},
  nagBarsPermanentlyDismissed: false,
  missedMeetings: 0,
  missedHighlights: 0,
  isLoading: true,
  isLegacyBilling: false,
})

export const buildDefaultUnifiedBillingState = (): UnifiedBillingState => ({
  paywall: buildDefaultPaywallState(),
  subscription: {
    active: false,
    isLegacyBilling: false,
  },
  billingProfile: null,
  usage: null,
  creditConsumption: null,
  lastUpdated: 0,
})

export const readBrowserBillingState = (): UnifiedBillingState => {
  try {
    const rawState = localStorage.getItem(BROWSER_BILLING_STATE_KEY)
    if (!rawState) {
      return buildDefaultUnifiedBillingState()
    }
    const parsedState = JSON.parse(rawState) as UnifiedBillingState
    if (!parsedState || typeof parsedState !== 'object') {
      return buildDefaultUnifiedBillingState()
    }
    return parsedState
  } catch (error) {
    console.error('[browser-runtime] Failed to read billing state', error)
    return buildDefaultUnifiedBillingState()
  }
}

const persistBrowserBillingState = (state: UnifiedBillingState): void => {
  try {
    localStorage.setItem(BROWSER_BILLING_STATE_KEY, JSON.stringify(state))
  } catch (error) {
    console.error('[browser-runtime] Failed to persist billing state', error)
  }
}

const setBrowserBillingState = (state: UnifiedBillingState): boolean => {
  try {
    persistBrowserBillingState(state)
    const billingStore = ensureStoreSnapshotNamespace('billing.store')
    billingStore.state = state
    emitBrowserMainStoreSet('billing.store', 'state', state)
    return true
  } catch (error) {
    console.error('[browser-runtime] Failed to set billing state', error)
    return false
  }
}

const calculateDaysRemaining = (endDate: string): number | null => {
  try {
    const now = new Date()
    const end = new Date(endDate)
    const diffTime = end.getTime() - now.getTime()
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  } catch (error) {
    console.error('[browser-runtime] Failed to calculate days remaining', error)
    return null
  }
}

type CreditBlock = {
  balance?: number
  initialBalance?: number
  expiryDate?: string
  effectiveDate?: string
}

const getValidCreditBlocks = (creditBlocks: Array<CreditBlock>, includePurchased = true): Array<CreditBlock> => {
  const now = new Date()
  return creditBlocks.filter((block) => {
    if (!block || typeof block !== 'object') {
      return false
    }
    const expiryDate = block.expiryDate ? new Date(block.expiryDate) : null
    const effectiveDate = block.effectiveDate ? new Date(block.effectiveDate) : null
    const balance = typeof block.balance === 'number' ? block.balance : 0
    const isPurchasedAndValid = !block.expiryDate && includePurchased && balance > 0
    const isRecurringAndValid = !!expiryDate && expiryDate > now
    return (isPurchasedAndValid || isRecurringAndValid) && !!effectiveDate && effectiveDate <= now
  })
}

const calculateTotalCredits = (creditBlocks: Array<CreditBlock>): number => {
  return creditBlocks.reduce((total, block) => total + (typeof block.balance === 'number' ? block.balance : 0), 0)
}

const calculateTotalInitialCredits = (creditBlocks: Array<CreditBlock>): number => {
  return creditBlocks.reduce(
    (total, block) => total + (typeof block.initialBalance === 'number' ? block.initialBalance : 0),
    0,
  )
}

/** The wire payload is untyped, so a malformed entry must read as "no change", not crash the copy that names it. */
const asScheduledPlanChange = (value: unknown): ScheduledPlanChange | null => {
  if (!value || typeof value !== 'object') return null
  const { plan, startsAt } = value as Record<string, unknown>
  return typeof plan === 'string' && typeof startsAt === 'string' ? { plan, startsAt } : null
}

const asCardOnFile = (value: unknown): CardOnFile | null => {
  if (!value || typeof value !== 'object') return null
  const { brand, last4 } = value as Record<string, unknown>
  return typeof brand === 'string' && typeof last4 === 'string' ? { brand, last4 } : null
}

const buildSubscriptionInfo = (
  subscriptionStatus: Record<string, unknown> | null,
  billingProfile: Record<string, unknown> | null,
): BillingSubscriptionState => {
  try {
    const hasLegacySubscription =
      subscriptionStatus?.active === true &&
      typeof subscriptionStatus?.plan === 'string' &&
      LEGACY_BILLING_PLANS.has(subscriptionStatus.plan)
    if (hasLegacySubscription) {
      return {
        ...(subscriptionStatus as BillingSubscriptionState),
        isLegacyBilling: true,
        active: true,
      }
    }

    if (billingProfile && typeof billingProfile === 'object') {
      const validCreditBlocks = getValidCreditBlocks(
        Array.isArray(billingProfile.creditBlocks) ? (billingProfile.creditBlocks as Array<CreditBlock>) : [],
        true,
      )
      const totalCredits = calculateTotalCredits(validCreditBlocks)
      const totalInitial = calculateTotalInitialCredits(validCreditBlocks)
      const usagePercentage =
        billingProfile.plan === 'free'
          ? 0
          : totalInitial > 0
            ? Math.floor(((totalInitial - totalCredits) / totalInitial) * FULL_USAGE_PERCENT)
            : totalCredits > 0
              ? 0
              : FULL_USAGE_PERCENT
      const isCancelled = !!billingProfile.canceled
      const currentPeriod = billingProfile.currentPeriod as { startedAt?: string } | undefined
      const periodEndAt = planEndsAt(billingProfile)
      return {
        active: billingProfile.plan !== 'free',
        plan: billingProfile.plan as string | undefined,
        currentPeriodEndAt: periodEndAt,
        startedAt: currentPeriod?.startedAt,
        credits: totalCredits,
        creditsUsedPercentage: usagePercentage,
        hasPaymentMethod: !!billingProfile.hasDefaultPaymentMethod,
        cardOnFile: asCardOnFile(billingProfile.cardOnFile),
        portalUrl: typeof billingProfile.portalUrl === 'string' ? billingProfile.portalUrl : null,
        isTrial: isTrialPlan(billingProfile.plan),
        cancelled: isCancelled,
        scheduledPlan: asScheduledPlanChange(billingProfile.scheduledPlan),
        daysUntilCancellation:
          isCancelled && typeof periodEndAt === 'string' ? calculateDaysRemaining(periodEndAt) : null,
        isLegacyBilling: false,
      }
    }

    if (subscriptionStatus && typeof subscriptionStatus === 'object') {
      return {
        ...(subscriptionStatus as BillingSubscriptionState),
        active: !!subscriptionStatus.active,
        isLegacyBilling: false,
      }
    }

    return {
      active: false,
      isLegacyBilling: false,
    }
  } catch (error) {
    console.error('[browser-runtime] Failed to build subscription info', error)
    return {
      active: false,
      isLegacyBilling: false,
    }
  }
}

type UsageMetric = {
  billableMetric?: { name?: string }
  usage?: Array<{ quantity?: number }>
  viewMode?: string
}

const buildPaywallState = (
  subscription: BillingSubscriptionState,
  billingProfile: Record<string, unknown> | null,
  usageData: Array<UsageMetric> | null,
  creditConsumption: { total?: string } | null,
): BillingPaywallState => {
  try {
    const defaultState = buildDefaultPaywallState()
    if (subscription.isLegacyBilling) {
      return {
        ...defaultState,
        plan: 'pro',
        credits: { used: 0, total: Number.POSITIVE_INFINITY, remaining: Number.POSITIVE_INFINITY, percentage: 0 },
        meetingHours: { used: 0, total: Number.POSITIVE_INFINITY, remaining: Number.POSITIVE_INFINITY, percentage: 0 },
        isLoading: false,
        isLegacyBilling: true,
      }
    }

    if (!billingProfile || typeof billingProfile !== 'object') {
      return {
        ...defaultState,
        plan: 'free',
        isLoading: false,
      }
    }

    const isFreePlan = billingProfile.plan === 'free'
    const allBlocks = Array.isArray(billingProfile.creditBlocks)
      ? (billingProfile.creditBlocks as Array<CreditBlock>)
      : []
    const validCreditBlocks = isFreePlan ? [] : getValidCreditBlocks(allBlocks)
    const totalRemainingCredits = isFreePlan ? 0 : calculateTotalCredits(validCreditBlocks)
    const totalInitialCredits = isFreePlan ? 0 : calculateTotalInitialCredits(validCreditBlocks)
    const consumedCredits =
      typeof creditConsumption?.total === 'string' ? Number.parseFloat(creditConsumption.total) : undefined
    const usedCredits = Number.isFinite(consumedCredits)
      ? (consumedCredits as number)
      : Math.max(0, totalInitialCredits - totalRemainingCredits)
    const totalAvailableCredits = isFreePlan ? 0 : totalRemainingCredits + usedCredits
    const creditPercentage = isFreePlan
      ? 0
      : totalAvailableCredits <= 0
        ? FULL_USAGE_PERCENT
        : Math.min(FULL_USAGE_PERCENT, Math.round((usedCredits / totalAvailableCredits) * FULL_USAGE_PERCENT))

    const meetingUsageMetric = (usageData ?? []).find(
      (item) => item?.billableMetric?.name === AUDIO_MINUTES_METRIC_NAME,
    )
    const meetingUsagePoints = Array.isArray(meetingUsageMetric?.usage) ? meetingUsageMetric.usage : []
    const meetingMinutesUsed =
      meetingUsageMetric?.viewMode === 'cumulative' && meetingUsagePoints.length > 0
        ? Number(meetingUsagePoints[meetingUsagePoints.length - 1]?.quantity ?? 0)
        : meetingUsagePoints.reduce((total: number, point) => total + Number(point?.quantity ?? 0), 0)
    const meetingHoursUsed = meetingMinutesUsed / MINUTES_PER_HOUR
    const meetingHoursTotal = getMeetingHoursForPlan(billingProfile.plan)
    const meetingHoursRemaining = meetingHoursTotal === null ? null : Math.max(0, meetingHoursTotal - meetingHoursUsed)
    // null total = unlimited (never at limit); 0 total = no allowance (at limit by definition)
    const meetingHoursPercentage =
      meetingHoursTotal === null
        ? 0
        : meetingHoursTotal === 0
          ? FULL_USAGE_PERCENT
          : Math.min(FULL_USAGE_PERCENT, Math.round((meetingHoursUsed / meetingHoursTotal) * FULL_USAGE_PERCENT))
    const isTrial =
      isTrialPlan(billingProfile.plan) || (billingProfile.plan === 'pro' && !billingProfile.hasDefaultPaymentMethod)

    return {
      ...defaultState,
      plan: billingProfile.plan as string | undefined,
      isTrial,
      trialEndsAt: isTrial ? planEndsAt(billingProfile) : undefined,
      credits: {
        used: usedCredits,
        total: totalInitialCredits,
        remaining: totalRemainingCredits,
        percentage: creditPercentage,
      },
      meetingHours: {
        used: meetingHoursUsed,
        total: meetingHoursTotal,
        remaining: meetingHoursRemaining,
        percentage: meetingHoursPercentage,
      },
      atWarning: !isFreePlan && creditPercentage >= CREDIT_WARNING_PERCENT && creditPercentage < FULL_USAGE_PERCENT,
      atLimit: !isFreePlan && (creditPercentage >= FULL_USAGE_PERCENT || totalRemainingCredits <= 0),
      isLoading: false,
      isLegacyBilling: false,
    }
  } catch (error) {
    console.error('[browser-runtime] Failed to build paywall state', error)
    return {
      ...buildDefaultPaywallState(),
      isLoading: false,
    }
  }
}

const fetchUnifiedBillingState = async (fresh: boolean): Promise<UnifiedBillingState | null> => {
  try {
    const query = fresh ? '?fresh=1' : ''
    const [billingProfileResponse, usageResponse, creditConsumptionResponse, subscriptionResponse] = await Promise.all([
      fetchBrowserApi(`/api/v1/billing${query}`),
      fetchBrowserApi(`/api/v1/billing/usage${query}`),
      fetchBrowserApi(`/api/v1/billing/credit-consumption${query}`),
      fetchBrowserApi('/api/v1/subscription'),
    ])

    if (!billingProfileResponse?.ok) {
      throw new Error(`Billing profile fetch failed: ${billingProfileResponse?.status ?? 'no response'}`)
    }

    const billingProfilePayload = await billingProfileResponse.json()
    const usagePayload = usageResponse?.ok ? await usageResponse.json() : null
    const creditConsumptionPayload = creditConsumptionResponse?.ok ? await creditConsumptionResponse.json() : null
    const subscriptionPayload = subscriptionResponse?.ok ? await subscriptionResponse.json() : null

    const billingProfile = unwrapSuccessData<Record<string, unknown>>(billingProfilePayload)
    if (!billingProfile) {
      throw new Error('Billing profile response was unsuccessful')
    }
    const usageData = unwrapSuccessData<Array<UsageMetric>>(usagePayload)
    const creditConsumptionList =
      unwrapSuccessData<Array<{ total?: string; timeframe_end?: string }>>(creditConsumptionPayload)
    const subscriptionStatus = unwrapSuccessData<Record<string, unknown>>(subscriptionPayload)

    const currentCreditConsumption = Array.isArray(creditConsumptionList)
      ? (creditConsumptionList.find(
          (item) => typeof item?.timeframe_end === 'string' && item.timeframe_end >= new Date().toISOString(),
        ) ??
        creditConsumptionList[0] ??
        null)
      : null

    const subscription = buildSubscriptionInfo(subscriptionStatus, billingProfile)
    const paywall = buildPaywallState(subscription, billingProfile, usageData ?? null, currentCreditConsumption)

    return {
      paywall,
      subscription,
      billingProfile,
      usage: (usageData as Array<Record<string, unknown>>) ?? null,
      creditConsumption: (currentCreditConsumption as Record<string, unknown>) ?? null,
      lastUpdated: Date.now(),
    }
  } catch (error) {
    console.error('[browser-runtime] Failed to fetch unified billing state', error)
    const existingState = ensureStoreSnapshotNamespace('billing.store').state
    if (!existingState || typeof existingState !== 'object') {
      setBrowserBillingState({
        ...buildDefaultUnifiedBillingState(),
        paywall: { ...buildDefaultPaywallState(), isLoading: false },
        lastUpdated: Date.now(),
      })
    }
    return null
  }
}

let isRefreshing = false
let queuedRefresh: {
  fresh: boolean
  promise: Promise<boolean>
  resolve: (published: boolean) => void
} | null = null

export const refreshBrowserBillingState = async (fresh = false): Promise<boolean> => {
  // A mutation during a read needs a fresh follow-up.
  if (isRefreshing) {
    if (!queuedRefresh) {
      let resolve!: (published: boolean) => void
      const promise = new Promise<boolean>((complete) => {
        resolve = complete
      })
      queuedRefresh = { fresh, promise, resolve }
    } else {
      queuedRefresh.fresh ||= fresh
    }
    return queuedRefresh.promise
  }
  isRefreshing = true
  try {
    const billingState = await fetchUnifiedBillingState(fresh)
    return billingState ? setBrowserBillingState(billingState) : false
  } catch (error) {
    console.error('[browser-runtime] Failed to refresh billing state', error)
    return false
  } finally {
    isRefreshing = false
    const queued = queuedRefresh
    queuedRefresh = null
    if (queued) {
      void refreshBrowserBillingState(queued.fresh).then(queued.resolve)
    }
  }
}
